import { escape } from 'lodash';
import moment from 'moment';
import sanitizeHtml from 'sanitize-html';
import { ref, computed, toRaw, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { defineStore } from 'pinia';

import { setAuthState } from '../composables/auth';
import { update } from '../api/profile.js';
import jobBoardApi from '../api/job-board.js';
import { getTailoredResume } from '../api/tailor-resume-cl.js';

import { useNotifications } from './notifications.store.js';
import { useSelectResumeStore } from './select-resume.store';
import { useJobAlertsStore } from './job-alerts.store.js';

import { formatNumberWithCommas, getFilterByValue, getStorageJson } from '../utils/helpers.js';
import { alertSearchFilters } from '../utils/job-alerts.js';

import { RANGE_OPTIONS, INDUSTRY_OPTIONS, WORK_OPTIONS, POSTED_OPTIONS, jobResultsTabs } from '../storage.js';
import { useIsMobile } from '../composables/isMobile.js';
import { SALARY_OPTIONS } from '../jobboard-v3/constants/salary.js';
import { EXPERIENCE_OPTIONS } from '../jobboard-v3/constants/experience.js';
import { CRITERION_LABELS, RELAXATION_STOP } from '../jobboard-v3/constants/criteria.js';
import {
	buildDefaultCriteria,
	filtersFromCriteriaPayload,
	toCriteriaPayload,
} from '../jobboard-v3/composables/useCriteria.js';
import { fetchGuestCriteria, readStoredCriteria, writeStoredCriteria } from '../jobboard-v3/composables/useGuestCriteria.js';
import { adoptGuestCriteria, fetchSavedSettings, persistCriteria } from '../jobboard-v3/composables/useSavedCriteria.js';
import { consumeJustRegistered } from '../utils/just-registered.js';

// Search-page store behavior is shared by both boards: v3 at /job-board/search
// (job-board-search) and classic at /job-board-classic/search (job-board-classic-search).
const SEARCH_ROUTE_NAMES = ['job-board-search', 'job-board-classic-search'];

const SAVED_REMOVAL_DOUBLE_CLICK_MS = 500;

const { getUser, setUser, isAuthenticated, userHaveAccessTo } = setAuthState();
const { isMobile } = useIsMobile();

const user = getUser();

export const useJobBoardStore = defineStore('jobBoard', () => {
	const route = useRoute();
	const router = useRouter();
	const notifications = useNotifications();
	const selectResumeStore = useSelectResumeStore();
	const jobAlertsStore = useJobAlertsStore();

	// State filters
	const jobTitle = ref(null);
	const location = ref(null);
	const selectedRange = ref(RANGE_OPTIONS[0]);
	const selectedIndustry = ref(INDUSTRY_OPTIONS[0]);
	const selectedWork = ref(WORK_OPTIONS[0]);
	const selectedPosted = ref(POSTED_OPTIONS[0]);

	const selectedIndustries = ref([]);
	const selectedWorkSettings = ref([]);

	const minDefaultExperience = 0;
	const maxDefaultExperience = 10;

	const minDefaultSalary = 20000;
	const maxDefaultSalary = 300000;

	const placeholderExperiences = ref('');
	const placeholderSalaries = ref('');

	const experiences = ref([minDefaultExperience, maxDefaultExperience]);
	const salaries = ref([minDefaultSalary, maxDefaultSalary]);
	const autoApplyFilter = ref(false);
	const autoApplyFilterParam = computed(() =>
		autoApplyFilter.value ? true : undefined
	);

	const savedJobs = ref([]);

	const filteredJobs = ref([]);
	const recommendedJobs = ref([]);
	const totalPages = ref(0);
	const isJobSearchFound = ref(false);
	const isRecommendedJobFound = ref(false);
	const showMobileJobDetails = ref(false);
	const foundJobs = ref(0);
	const isRecommendedJobsLoaded = ref(false);
	const isRecommendedJobsInProgress = ref(false);
	const selectedJob = ref(null);
	const selectedSavedJob = ref(null);
	// The Saved tab's order, by date saved (EN-1302). It lasts the session, like the list.
	const isSavedSortNewestFirst = ref(true);
	const isSortByDateDescendent = ref(true);
	// v3 board only. A `sort_by` outranks the relevance score server-side (EN-905), so the
	// board sends one only when the seeker picked a date order (EN-1002).
	const isSortByRelevance = ref(true);
	const isSortByDateClicked = ref(false);
	const isFirstSearch = ref(true);
	const isSharedLinkFound = ref(false);
	const removedJobs = ref([]);

	const loading = ref(false);
	const isSavedJobsLoading = ref(false);
	const lazyLoading = ref(false);
	const isSavedJobsLoaded = ref(false);
	const generatingDocuments = ref(false);

	const autocomplete = ref(null);
	const geocoder = ref(null);
	const latitude = ref(null);
	const longitude = ref(null);
	const isJobBoardAiOnboardingPassed = ref(user?.options?.job_board_onboarding_passed || false);
	const isJobBoardFirstRunSeen = ref(user?.options?.job_board_first_run_seen || false);
	const isUsLocationBannerDismissed = ref(user?.options?.dismissed_us_location_banner || false);
	const infiniteScrollingPage = ref(1);
	const isGeoDataLoading = ref(false);
	const isLocationSelected = ref(false);
	const geoDataPromise = ref(null);
	const lastResolvedLocation = ref('');
	let geoGeneration = 0;
	const queryId = ref(null);
	// The query id of the search a Job Alert opened (EN-1252), set by `markAlertSearch()`.
	let alertQueryId = null;
	let searchRetryPending = false;
	let searchV3RetryPending = false;
	// The Saved removal whose toast is still up: { job, index, wasSelected, toastId }. Its DELETE goes
	// out when the toast closes, unless Undo ran first.
	let pendingSavedRemoval = null;
	// When the last Saved removal happened. The neighbour slides under the cursor as a job goes, so a removal right
	// after is the second click of a double-click, not a choice to remove the neighbour too (EN-1301).
	let lastSavedRemovalAt = 0;
	// What the page-1 search in flight was asked for, to tell a changed filter from a repeat.
	let searchV3InFlightKey = null;

	/**
	 * Saved in the order on screen: by date saved, then by the saved row's id, which is how the endpoint breaks a
	 * tie. `savedJobs` keeps the positions Undo and rollbacks put jobs back at, so it is never reordered.
	 */
	const sortedSavedJobs = computed(() => {
		const oldestFirst = [...savedJobs.value].sort(
			(a, b) => (Date.parse(a.saved_at) - Date.parse(b.saved_at)) || (a.id - b.id),
		);

		return isSavedSortNewestFirst.value ? oldestFirst.reverse() : oldestFirst;
	});

	const toggleSavedSort = () => {
		isSavedSortNewestFirst.value = !isSavedSortNewestFirst.value;
	};

	const isSearchTab = computed(() => currentTab.value === jobResultsTabs.search);
	const isRecommendedTab = computed(() => currentTab.value === jobResultsTabs.recommended);
	const isJobFound = computed(() => (isSearchTab.value ? isJobSearchFound.value : isRecommendedJobFound.value));

	const mountPageLoading = ref(true);
	const currentTab = ref(null);

	const workSettingValues = {
		'in-person': 'In person',
		remote: 'Remote',
		hybrid: 'Hybrid',
	};

	const isSearchButtonDisabled = computed(
		() => jobTitle.value == null || jobTitle.value.length < 3 || jobTitle.value === ''
	);

	// Live filters (jobboard-v3) must be able to search with an empty job title as long as
	// some other filter is active — unlike the old Search button, which requires typed text.
	// Only a short, incomplete title (1-2 chars) should hold off the debounced auto-search.
	const isSearchTextTooShort = computed(() => {
		const length = jobTitle.value?.length ?? 0;

		return length > 0 && length < 3;
	});

	// Whether there's anything to search on: typed text/location, or any filter away from
	// its default. Drives the default-vs-active-state split for the live filter shell —
	// when this goes false, searchJobsHandler() backs out of the search tab instead of
	// showing an unfiltered "all jobs" result set.
	const hasActiveSearchOrFilters = computed(() => {
		const conditions = [
			(jobTitle.value?.trim() ?? '').length > 0,
			(location.value?.trim() ?? '').length > 0,
			selectedIndustry.value?.value != null,
			selectedWork.value?.value != null,
			selectedIndustries.value.length > 0,
			selectedWorkSettings.value.length > 0,
			selectedPosted.value?.value != null,
			selectedRange.value?.value != null,
			experiences.value[0] !== minDefaultExperience,
			experiences.value[1] !== maxDefaultExperience,
			salaries.value[0] !== minDefaultSalary,
			salaries.value[1] !== maxDefaultSalary,
			autoApplyFilter.value === true,
		];

		return conditions.some(Boolean);
	});

	const activeFilterCount = computed(() => {
		const flags = [
			(location.value?.trim() ?? '').length > 0,
			selectedRange.value?.value != null,
			selectedIndustries.value.length > 0,
			selectedWorkSettings.value.length > 0,
			selectedPosted.value?.value != null,
			experiences.value[0] !== minDefaultExperience || experiences.value[1] !== maxDefaultExperience,
			salaries.value[0] !== minDefaultSalary || salaries.value[1] !== maxDefaultSalary,
		];

		return flags.filter(Boolean).length;
	});

	const formatMultiSelectChipLabel = (items) => {
		const [first, ...rest] = items;

		return rest.length > 0 ? `${first.title} +${rest.length}` : first.title;
	};

	// Removable chips shown below the mobile search row once a search/filter is active.
	// Mirrors activeFilterCount's field list but carries the display label per filter.
	const activeFilterChips = computed(() => {
		const candidates = [
			{
				key: 'location',
				active: (location.value?.trim() ?? '').length > 0,
				label: () => location.value,
			},
			{
				key: 'range',
				active: selectedRange.value?.value != null,
				label: () => selectedRange.value.title,
			},
			{
				key: 'industries',
				active: selectedIndustries.value.length > 0,
				label: () => formatMultiSelectChipLabel(selectedIndustries.value),
			},
			{
				key: 'workSettings',
				active: selectedWorkSettings.value.length > 0,
				label: () => formatMultiSelectChipLabel(selectedWorkSettings.value),
			},
			{
				key: 'posted',
				active: selectedPosted.value?.value != null,
				label: () => selectedPosted.value.title,
			},
			{
				key: 'experience',
				active: experiences.value[0] !== minDefaultExperience || experiences.value[1] !== maxDefaultExperience,
				label: () => (
					EXPERIENCE_OPTIONS.find((item) => item.value === experiences.value[0]) ?? EXPERIENCE_OPTIONS[0]
				).title,
			},
			{
				key: 'salary',
				active: salaries.value[0] !== minDefaultSalary || salaries.value[1] !== maxDefaultSalary,
				label: () => (
					SALARY_OPTIONS.find((item) => item.value === salaries.value[0]) ?? SALARY_OPTIONS[0]
				).title,
			},
		];

		return candidates
			.filter((candidate) => candidate.active)
			.map((candidate) => ({ key: candidate.key, label: candidate.label() }));
	});

	/**
	 * Decorate a page of results, stamping each job with the search that surfaced it.
	 *
	 * `criteriaQueryId` and `listPosition` travel on the job rather than being read from the
	 * shared `queryId` ref at click time (EN-838). The ref is one value shared by the search
	 * and recommended tabs, and switching tabs does not refetch — so a click after a tab
	 * round-trip would otherwise be attributed to whichever list loaded last. Stamping at map
	 * time is correct for any number of lists, and survives a job being clicked long after.
	 *
	 * @param {object[]} jobsList the page of jobs from the response
	 * @param {number|null} responseQueryId the response's `query`, absent past page 1
	 * @param {number} offset how many jobs are already in the list, for absolute positions
	 */
	const markAdditionalFields = (jobsList, responseQueryId = null, offset = 0) => {
		return jobsList.map((job, index) => {
			const date = moment(job?.posted_at);
			const cards = getFormattedCardFilters(job);
			return {
				...job,
				timeAgoPosted: date.fromNow(),
				cards: cards,
				daysAgoPosted: date.diff(moment(), 'days'),
				criteriaQueryId: responseQueryId ?? queryId.value ?? null,
				listPosition: offset + index,
			};
		});
	};

	const getExperiencesForRequest = () => {
		const start = experiences.value[0] === minDefaultExperience ? null : experiences.value[0];
		const end = experiences.value[1] === maxDefaultExperience ? null : experiences.value[1];

		return [start, end];
	};

	const getSalariesForRequest = () => {
		const start =
            salaries.value[0] === minDefaultSalary && salaries.value[1] === maxDefaultSalary ? null : salaries.value[0];
		const end = salaries.value[1] === maxDefaultSalary ? null : salaries.value[1];

		return [start, end];
	};

	const setCurrentTab = async (tab) => {
		if (tab === jobResultsTabs.recommended && recommendedJobs.value.length !== 0) {
			selectedJob.value = recommendedJobs.value[0];
		}
		else if ( tab === jobResultsTabs.search && filteredJobs.value.length !== 0) {
			selectedJob.value = filteredJobs.value[0];
		}

		currentTab.value = tab;
	};

	const SEARCH_FILTERS_STORAGE_KEY = 'jobBoardSearchFilters';

	const persistSearchFilters = () => {
		try {
			const payload = {
				jobTitle: jobTitle.value,
				location: location.value,
				range: selectedRange.value?.value ?? null,
				industry: selectedIndustry.value?.value ?? null,
				work: selectedWork.value?.value ?? null,
				industries: selectedIndustries.value.map((item) => item.value),
				workSettings: selectedWorkSettings.value.map((item) => item.value),
				posted: selectedPosted.value?.value ?? null,
				experiences: experiences.value,
				salaries: salaries.value,
				latitude: latitude.value,
				longitude: longitude.value,
				isSortByDateDescendent: isSortByDateDescendent.value,
				isSortByRelevance: isSortByRelevance.value,
				autoApplyFilter: autoApplyFilter.value,
			};
			localStorage.setItem(SEARCH_FILTERS_STORAGE_KEY, JSON.stringify(payload));
		} catch {
			// localStorage may be unavailable (private mode / quota) — persistence is best-effort
		}
	};

	// A persisted job title only ever reaches storage via a successful search, which
	// requires it to be either empty (filter-only search) or 3+ chars — never 1-2 chars.
	const hasStoredJobTitle = (stored) => typeof stored.jobTitle === 'string' && stored.jobTitle.trim().length >= 3;

	// Mirrors hasActiveSearchOrFilters, but against a persisted payload rather than live refs —
	// a filter-only search (no job title) must still be restorable.
	const hasStoredFilters = (stored) => [
		typeof stored.location === 'string' && stored.location.trim().length > 0,
		stored.industry != null,
		stored.work != null,
		Array.isArray(stored.industries) && stored.industries.length > 0,
		Array.isArray(stored.workSettings) && stored.workSettings.length > 0,
		stored.posted != null,
		stored.range != null,
		Array.isArray(stored.experiences)
			&& (stored.experiences[0] !== minDefaultExperience || stored.experiences[1] !== maxDefaultExperience),
		Array.isArray(stored.salaries)
			&& (stored.salaries[0] !== minDefaultSalary || stored.salaries[1] !== maxDefaultSalary),
		stored.autoApplyFilter === true,
	].some(Boolean);

	const mapStoredOptions = (options, storedValues) => {
		if (!Array.isArray(storedValues)) {
			return [];
		}

		return storedValues
			.map((value) => options.find((option) => option.value === value))
			.filter(Boolean);
	};

	const restoreSearchFilters = () => {
		const stored = getStorageJson(SEARCH_FILTERS_STORAGE_KEY, null);

		if (!stored || (!hasStoredJobTitle(stored) && !hasStoredFilters(stored))) {
			return false;
		}

		jobTitle.value = stored.jobTitle ?? null;
		location.value = stored.location ?? null;
		selectedRange.value = getFilterByValue(RANGE_OPTIONS, stored.range) ?? RANGE_OPTIONS[0];
		selectedIndustry.value = getFilterByValue(INDUSTRY_OPTIONS, stored.industry) ?? INDUSTRY_OPTIONS[0];
		selectedWork.value = getFilterByValue(WORK_OPTIONS, stored.work) ?? WORK_OPTIONS[0];
		selectedIndustries.value = mapStoredOptions(INDUSTRY_OPTIONS, stored.industries);
		selectedWorkSettings.value = mapStoredOptions(WORK_OPTIONS, stored.workSettings);
		selectedPosted.value = getFilterByValue(POSTED_OPTIONS, stored.posted) ?? POSTED_OPTIONS[0];
		experiences.value = Array.isArray(stored.experiences)
			? stored.experiences
			: [minDefaultExperience, maxDefaultExperience];
		salaries.value = Array.isArray(stored.salaries) ? stored.salaries : [minDefaultSalary, maxDefaultSalary];
		latitude.value = stored.latitude ?? null;
		longitude.value = stored.longitude ?? null;
		// Mark the restored location as resolved so the input watcher does not
		// re-geocode it on page load and clobber the restored coordinates.
		lastResolvedLocation.value = stored.location ?? '';
		isSortByDateDescendent.value = stored.isSortByDateDescendent ?? true;
		isSortByRelevance.value = stored.isSortByRelevance ?? true;
		autoApplyFilter.value = stored.autoApplyFilter ?? false;

		return true;
	};

	const searchJobs = async (lazyLoad = false) => {
		if (loading.value || lazyLoading.value) {
			if (!lazyLoad) {searchRetryPending = true;}
			return;
		}

		try {
			if (lazyLoad) {
				if (infiniteScrollingPage.value === totalPages.value) {
					return;
				}

				infiniteScrollingPage.value += 1;
				lazyLoading.value = true;
			} else {
				infiniteScrollingPage.value = 1;
				loading.value = true;
			}
			isFirstSearch.value = false;

			const experiencesForRequest = getExperiencesForRequest();
			const salariesForRequest = getSalariesForRequest();

			const jobSearchFilters = {
				job_title: jobTitle.value,
				location: location.value,
				work_setting: selectedWork.value.value,
				last_posted: selectedPosted.value.value,
				longitude: longitude.value ? parseFloat(longitude.value) : null,
				latitude: latitude.value ? parseFloat(latitude.value) : null,
				page: infiniteScrollingPage.value,
				jobs_per_page: 30,
				sort_by: 'posted_at',
				sort_direction: isSortByDateDescendent.value ? 'desc' : 'asc',
				industry: selectedIndustry.value.value,
				radius: selectedRange.value.value ? parseInt(selectedRange.value.value) : null,
				min_experience: experiencesForRequest[0],
				max_experience: experiencesForRequest[1],
				min_salary: salariesForRequest[0],
				max_salary: salariesForRequest[1],
				auto_apply_filter: autoApplyFilterParam.value,
			};

			const result = await jobBoardApi.findJobsJobBoard(jobSearchFilters);
			const jobResults = result?.jobs ?? [];
			totalPages.value = result?.pages_total;

			if (result.query) {
				queryId.value = result.query;
			}

			isJobSearchFound.value = jobResults.length > 0;
			foundJobs.value = result.jobs_count ?? 0;

			if (isJobSearchFound.value) {
				if (lazyLoad) {
					filteredJobs.value.push(
						...markAdditionalFields(jobResults, result.query ?? null, filteredJobs.value.length),
					);
				} else {
					filteredJobs.value = markAdditionalFields(jobResults, result.query ?? null);
					persistSearchFilters();
				}

				if (!isMobile.value) {setSelectedJob(filteredJobs.value[0]);}
			} else {
				isJobSearchFound.value = false;
				filteredJobs.value = [];
			}

			await jobAlertsStore.checkIfJobAlertExist(jobSearchFilters);
		} catch {
			const message = lazyLoad
				? 'Failed to load more jobs.'
				: isSortByDateClicked.value
					? 'Failed to sort jobs.'
					: 'Failed to find jobs.';
			notifications.notify(message, 'Error');
			isJobSearchFound.value = false;
			totalPages.value = 0;
		} finally {
			loading.value = false;
			lazyLoading.value = false;
			isSortByDateClicked.value = false;

			if (searchRetryPending) {
				searchRetryPending = false;
				searchJobs();
			}
		}
	};

	const searchJobsHandler = async () => {
		if (isSearchTextTooShort.value) {return;}

		if (isSharedLinkFound.value) {
			router.push({ name: 'job-board-search' });
			isSharedLinkFound.value = false;
		}

		if (!hasActiveSearchOrFilters.value) {
			localStorage.removeItem(SEARCH_FILTERS_STORAGE_KEY);

			if (isAuthenticated()) {
				setCurrentTab(jobResultsTabs.recommended);
			} else {
				setCurrentTab(jobResultsTabs.search);
				isFirstSearch.value = true;
				filteredJobs.value = [];
				isJobSearchFound.value = false;
			}

			return;
		}

		try {
			await nextTick(); // Ensure DOM updates before searching
			if (geoDataPromise.value) {
				try { await geoDataPromise.value; } catch { /* getGeoData handles its own UX */ }
			}
			setCurrentTab(jobResultsTabs.search);
			await searchJobs();
		} catch (error) {
			console.error('Error searching jobs:', error);
		}
	};

	// The algorithm find-jobs should run this search on, or null to send the flat filters and
	// let the server adapt them. Null is what keeps the classic board out of this entirely:
	// `searchJobs()` never reads it, so v1 stays on flat params whatever is set here.
	const searchCriteria = ref(null);

	// Whether the Build-Your-Own-Algorithm surface is live for this caller. A visitor has no
	// `features` map — `Feature::all()` ships only on the user resource — so signed out it
	// arrives from the public defaults endpoint instead of the flag.
	const criteriaEnabled = ref(false);
	// EN-1066: the seeker's "Complete Auto Apply" opt-in, saved with the Job Board Settings panel.
	const completeAutoApply = ref(false);

	// The `relaxation` block each surface last measured, or null when the response carried
	// none (the legacy path never does, which is what makes the key safe to feature-detect on).
	//
	// One per tab rather than one shared ref: recommendations are cached across tab switches,
	// so a single ref would show the search's banner over the recommended list.
	const searchRelaxation = ref(null);
	const recommendationsRelaxation = ref(null);

	// The relaxation level the seeker accepted from a suggestion (EN-849), echoed on the next
	// recommendations request. Session state on purpose: accepting drops a requirement for the
	// feed they are looking at, not from the algorithm they saved, so there is nothing to
	// persist and nothing to undo. Cleared whenever the algorithm underneath it changes — a
	// level computed against one set means something else against another.
	const approvedRelaxationLevel = ref(null);

	/**
	 * What the banner should say about how this feed was loosened, or null for nothing.
	 *
	 * @returns {{kind: 'relaxed', names: string[], added: number}
	 *   | {kind: 'low', total: number, stop: string}
	 *   | null}
	 */
	const relaxationNotice = computed(() => {
		const meta = isRecommendedTab.value ? recommendationsRelaxation.value : searchRelaxation.value;

		if (!meta) {
			return null;
		}

		// The discriminator is the stop reason, not an empty `relaxed`: a seeker whose hard
		// bucket alone falls under the floor can still have had a ranked stack spent on them.
		//
		// Zero is left to the results area's own empty state rather than phrased as "only 0
		// jobs" above it. EN-849 owns that state and replaces it with the specific suggestion.
		if (meta.stop !== RELAXATION_STOP.FLOOR_MET) {
			return meta.total > 0 ? { kind: 'low', total: meta.total, stop: meta.stop } : null;
		}

		// Unmeasured levels carry `added: null` (inert, or never run) and name nothing.
		const names = (meta.relaxed ?? [])
			.filter((step) => step.added > 0)
			.map((step) => CRITERION_LABELS[step.key])
			.filter(Boolean);

		// The aggregate, not the sum of the named steps: deltas are signed, so a rung can
		// legitimately come back smaller once the k-NN pre-filter is regenerated.
		const added = meta.total - (meta.baseline_total ?? meta.total);

		if (names.length === 0 || added <= 0) {
			return null;
		}

		return { kind: 'relaxed', names, added };
	});

	/**
	 * The one requirement worth offering to drop, or null when there is nothing honest to say.
	 *
	 * The engine measured this rung without taking it (EN-849), so everything here is a
	 * hypothetical the seeker has to accept before anything changes. Four things have to hold
	 * before it is worth putting in front of them, and each rules out a different lie:
	 *
	 *   - a `suggestion` at all — the flag-off response carries no relaxation block, and an
	 *     exhausted stack carries the block with a null suggestion;
	 *   - a **positive** `added`, because deltas are signed: regenerating the k-NN pre-filter
	 *     can make a wider rung come back smaller, and "would show -4 more jobs" is not an offer;
	 *   - a count still under the floor, so a feed that filled up between the probe and the
	 *     render is not pestered;
	 *   - a label we have, so an unknown key from a later contract version renders nothing
	 *     rather than "Removing undefined".
	 *
	 * @returns {{key: string, label: string, level: number, added: number}|null}
	 */
	const relaxationSuggestion = computed(() => {
		const meta = isRecommendedTab.value ? recommendationsRelaxation.value : searchRelaxation.value;
		const suggestion = meta?.suggestion;

		if (!suggestion || suggestion.added <= 0 || meta.total >= meta.floor) {
			return null;
		}

		const label = CRITERION_LABELS[suggestion.key];

		return label
			? { key: suggestion.key, label, level: suggestion.level, added: suggestion.added }
			: null;
	});

	/**
	 * Take the offer: re-run the feed at the level that drops the suggested requirement.
	 *
	 * The level is echoed rather than the criterion named, so the seeker gets exactly the
	 * search the suggestion promised them — the same copy-it-back round trip pagination
	 * makes. Nothing is saved: see `approvedRelaxationLevel`.
	 */
	const approveRelaxationSuggestion = async () => {
		const level = relaxationSuggestion.value?.level;

		if (level == null) {
			return;
		}

		approvedRelaxationLevel.value = level;
		await reloadRecommendations();
	};

	// The filter values the criteria panel reads — for the lock state it shows, and for the
	// `value` on each criterion, which the panel itself never edits.
	const criteriaFilterState = computed(() => ({
		jobTitle: jobTitle.value,
		location: location.value,
		latitude: latitude.value,
		longitude: longitude.value,
		selectedRange: selectedRange.value,
		selectedIndustries: selectedIndustries.value,
		selectedWorkSettings: selectedWorkSettings.value,
		selectedPosted: selectedPosted.value,
		experiences: experiences.value,
		salaries: salaries.value,
		autoApplyFilter: autoApplyFilter.value,
	}));

	// Everything a page-1 v3 search sends: the filters, the sort and the algorithm they run under.
	const searchV3Key = () => JSON.stringify([
		criteriaFilterState.value,
		isSortByRelevance.value,
		isSortByDateDescendent.value,
		searchCriteria.value,
	]);

	/**
	 * The same search expressed in the criteria vocabulary.
	 *
	 * A swap, not an addition: `JobBoardFindJobsRequest` rejects a request carrying both a
	 * criteria payload and any of the flat filters, because the two describe the same search
	 * and there is no honest way to reconcile them when they disagree. Every filter value
	 * travels inside the payload instead; only the transport parameters carry over.
	 *
	 * Page 2 onwards echoes back the level page 1 settled on, so every page of one search
	 * shares a relaxation state. Without it the ladder re-runs per page and can settle
	 * somewhere the banner does not name.
	 */
	const toCriteriaRequest = (filters) => ({
		...toCriteriaPayload(searchCriteria.value, criteriaFilterState.value),
		page: filters.page,
		jobs_per_page: filters.jobs_per_page,
		...(filters.sort_by ? { sort_by: filters.sort_by, sort_direction: filters.sort_direction } : {}),
		...(filters.page > 1 && searchRelaxation.value != null
			? { relaxation_level: searchRelaxation.value.relaxation_level }
			: {}),
	});

	/**
	 * Open the board on the algorithm this caller should be searching under.
	 *
	 * `criteriaEnabled` is flipped only after the set has landed, so the panel can never be
	 * rendered against a null one — the drawer takes `criteria` as a required prop and the page
	 * mounts it on this flag.
	 *
	 * Every failure path falls back to the default stack rather than rejecting: the panel is an
	 * enhancement, and a seeker whose request failed should get the board with the default order
	 * rather than a broken one.
	 */
	const loadCriteria = async () => {
		if (!isAuthenticated()) {
			const { enabled, criteria } = await fetchGuestCriteria();

			if (!enabled) {
				return;
			}

			searchCriteria.value = readStoredCriteria() ?? criteria ?? buildDefaultCriteria();
			criteriaEnabled.value = true;

			return;
		}

		if (!userHaveAccessTo('criteria-weighted-search')) {
			return;
		}

		// The handoff from the signed-out half (EN-859): a set built before this seeker
		// registered is carried into their settings on this first authenticated visit. Login is
		// deliberately not a carry-over — a returning seeker's saved algorithm outranks whatever
		// this browser happens to remember.
		const adopted = consumeJustRegistered() ? await adoptGuestCriteria() : null;

		const saved = await fetchSavedSettings();

		completeAutoApply.value = saved?.completeAutoApply ?? false;
		searchCriteria.value = adopted ?? saved?.criteria ?? buildDefaultCriteria();
		criteriaEnabled.value = true;
	};

	/**
	 * Write a seeded criteria payload's values into the flat filters.
	 *
	 * A replacement, not a merge: every criteria-backed filter is set from the payload and a
	 * key the seed had nothing to say about resets to its default. "Recommend for me" builds a
	 * whole search, and leaving a stale filter interleaved with seeded ones would hand back a
	 * set the seeker never chose and cannot account for.
	 *
	 * @param {object} filters partial flat filter state from `filtersFromCriteriaPayload`
	 */
	const applyRecommendedFilters = (filters) => {
		jobTitle.value = filters.jobTitle ?? null;
		location.value = filters.location ?? null;
		latitude.value = filters.latitude ?? null;
		longitude.value = filters.longitude ?? null;
		selectedRange.value = getFilterByValue(RANGE_OPTIONS, filters.range) ?? RANGE_OPTIONS[0];
		selectedPosted.value = getFilterByValue(POSTED_OPTIONS, filters.posted) ?? POSTED_OPTIONS[0];
		selectedIndustries.value = mapStoredOptions(INDUSTRY_OPTIONS, filters.industries);
		selectedWorkSettings.value = mapStoredOptions(WORK_OPTIONS, filters.workSettings);
		experiences.value = filters.experiences ?? [minDefaultExperience, maxDefaultExperience];
		salaries.value = filters.salaries ?? [minDefaultSalary, maxDefaultSalary];
		autoApplyFilter.value = filters.autoApplyFilter ?? false;

		// The server already geocoded this location. Marking it resolved is what stops the
		// location watcher re-geocoding it and clobbering those coordinates — the same reason
		// `restoreSearchFilters` does it.
		lastResolvedLocation.value = filters.location ?? '';
	};

	/**
	 * Write a saved job alert's search into the flat filters, for "View Matching Jobs" (EN-1304).
	 *
	 * A replacement, like `applyRecommendedFilters`: the board shows the alert's search and nothing the seeker had
	 * set before. With weighted criteria on, every filter the alert sets is an active filter, so `filterLockedKeys`
	 * makes each a Must-have, which is how the alert's email searches.
	 *
	 * @param {object} alert a saved alert as the subscriptions endpoint returns it
	 */
	const applyJobAlertFilters = (alert) => {
		const filters = alertSearchFilters(alert);

		jobTitle.value = filters.jobTitle;
		location.value = filters.location;
		latitude.value = filters.latitude;
		longitude.value = filters.longitude;
		selectedRange.value = getFilterByValue(RANGE_OPTIONS, filters.radius) ?? RANGE_OPTIONS[0];
		selectedPosted.value = getFilterByValue(POSTED_OPTIONS, filters.posted) ?? POSTED_OPTIONS[0];
		selectedIndustry.value = INDUSTRY_OPTIONS[0];
		selectedWork.value = WORK_OPTIONS[0];
		selectedIndustries.value = filters.industries;
		selectedWorkSettings.value = filters.workSettings;
		experiences.value = filters.experiences;
		salaries.value = filters.salaries;
		autoApplyFilter.value = filters.autoApplyFilter;
		approvedRelaxationLevel.value = null;

		// The alert carries the coordinates its search was geocoded to. Marking the location resolved is what stops
		// the location watcher geocoding it again — the same reason `restoreSearchFilters` does it.
		lastResolvedLocation.value = filters.location ?? '';
	};

	/**
	 * Build this seeker a search from their resume, and hand its values to the panel to adjust.
	 *
	 * Only the values are taken. The seed is the default stack with every key ranked, so
	 * adopting its buckets and order would undo whatever the seeker had locked or dragged
	 * (EN-995); leaving `searchCriteria` alone also leaves the panel's unsaved draft alone.
	 *
	 * Neither persists nor searches: the seeker gets to look at what we came up with before
	 * committing to it, and the panel's own Save already does both.
	 *
	 * Rejects rather than degrading — unlike `loadCriteria`, this runs because the seeker
	 * pressed a button, and a click that silently does nothing is worse than an error.
	 *
	 * @returns {Promise<{has_resume: boolean}>} the seeded payload, for the caller's messaging
	 */
	const applyRecommendedCriteria = async () => {
		const recommended = await jobBoardApi.getRecommendedCriteria();

		applyRecommendedFilters(filtersFromCriteriaPayload(recommended));
		approvedRelaxationLevel.value = null;

		return recommended;
	};

	/**
	 * Apply the seeker's edited algorithm and put it somewhere it will survive a reload.
	 *
	 * Applied before it is stored, and left applied when the write fails: the set drives this
	 * session's ranking either way, and losing the edit on top of losing the save would be the
	 * worse of the two failures. Signed out there is no settings row to write to, so the wire
	 * payload goes to localStorage until they register.
	 *
	 * @param {Array<{key: string, userBucket: string}>} criteria the panel's edited set
	 * @param {{version: number, criteria: Array<object>}} payload the same set on the wire
	 */
	const saveCriteria = async (criteria, payload, { completeAutoApply: completeAutoApplyChoice } = {}) => {
		searchCriteria.value = criteria;
		// A level the seeker accepted was measured against the set they just replaced.
		approvedRelaxationLevel.value = null;

		if (!isAuthenticated()) {
			writeStoredCriteria(payload);
			await searchJobsHandlerV3();

			return;
		}

		try {
			searchCriteria.value = await persistCriteria(criteria, { completeAutoApply: completeAutoApplyChoice });

			if (typeof completeAutoApplyChoice === 'boolean') {
				completeAutoApply.value = completeAutoApplyChoice;
			}
		} catch {
			notifications.notify('Failed to save your recommendation settings.', 'Error');
		}

		// Recommendations reads the saved set server-side, so whatever is cached was produced by
		// the previous algorithm. Invalidated for the tab switch, re-fetched now when it is the
		// tab the seeker is looking at.
		isRecommendedJobsLoaded.value = false;

		if (isRecommendedTab.value) {
			await reloadRecommendations();

			return;
		}

		await searchJobsHandlerV3();
	};

	const searchJobsV3 = async (lazyLoad = false) => {
		if (loading.value || lazyLoading.value) {
			// A filter changed while a search was already in flight — that request will
			// resolve with stale filters, so flag a follow-up once it's done rather than
			// silently losing this change. Pagination requests aren't worth replaying, and
			// neither is a call that would only repeat the page-1 search in flight: writing a
			// whole search's filters at once (EN-1304's "View Matching Jobs") also fires each
			// filter control's own search, and every search writes a search-history row.
			if (!lazyLoad && (lazyLoading.value || searchV3Key() !== searchV3InFlightKey)) {
				searchV3RetryPending = true;
			}
			return;
		}

		try {
			if (lazyLoad) {
				if (infiniteScrollingPage.value === totalPages.value) {
					return;
				}

				infiniteScrollingPage.value += 1;
				lazyLoading.value = true;
			} else {
				infiniteScrollingPage.value = 1;
				loading.value = true;
				searchV3InFlightKey = searchV3Key();
			}

			isFirstSearch.value = false;

			const experiencesForRequest = getExperiencesForRequest();
			const salariesForRequest = getSalariesForRequest();

			const jobSearchFilters = {
				job_title: jobTitle.value,
				location: location.value,
				work_setting: selectedWorkSettings.value.map((item) => item.value),
				last_posted: selectedPosted.value.value,
				longitude: longitude.value ? parseFloat(longitude.value) : null,
				latitude: latitude.value ? parseFloat(latitude.value) : null,
				page: infiniteScrollingPage.value,
				jobs_per_page: 30,
				...(isSortByRelevance.value
					? {}
					: { sort_by: 'posted_at', sort_direction: isSortByDateDescendent.value ? 'desc' : 'asc' }),
				industry: selectedIndustries.value.map((item) => item.value),
				radius: selectedRange.value.value ? parseInt(selectedRange.value.value) : null,
				min_experience: experiencesForRequest[0],
				max_experience: experiencesForRequest[1],
				min_salary: salariesForRequest[0],
				max_salary: salariesForRequest[1],
				auto_apply_filter: autoApplyFilterParam.value,
			};

			const result = await jobBoardApi.findJobsJobBoard(
				searchCriteria.value === null
					? jobSearchFilters
					: toCriteriaRequest(jobSearchFilters),
			);
			const jobResults = result?.jobs ?? [];
			totalPages.value = result?.pages_total;

			if (result.query) {
				queryId.value = result.query;
			}

			// Page 1 only. A paged response comes back `pinned` — it never consulted the floor,
			// so it has no banner of its own and must keep the one page 1 resolved.
			if (!lazyLoad) {
				searchRelaxation.value = result?.relaxation ?? null;
			}

			isJobSearchFound.value = jobResults.length > 0;
			// Always reflect the latest response's count, even on zero results — otherwise
			// the results header (visible even with no matches) shows a stale prior count.
			foundJobs.value = result.jobs_count ?? 0;

			if (isJobSearchFound.value) {
				if (lazyLoad) {
					filteredJobs.value.push(
						...markAdditionalFields(jobResults, result.query ?? null, filteredJobs.value.length),
					);
				} else {
					filteredJobs.value = markAdditionalFields(jobResults, result.query ?? null);
					persistSearchFilters();
				}

				if (!isMobile.value) {setSelectedJob(filteredJobs.value[0]);}
			} else {
				isJobSearchFound.value = false;
				filteredJobs.value = [];
			}

			await jobAlertsStore.checkIfJobAlertExist(jobSearchFilters);
		} catch {
			const message = lazyLoad
				? 'Failed to load more jobs.'
				: isSortByDateClicked.value
					? 'Failed to sort jobs.'
					: 'Failed to find jobs.';
			notifications.notify(message, 'Error');
			isJobSearchFound.value = false;
			totalPages.value = 0;

			if (!lazyLoad) {
				searchRelaxation.value = null;
			}
		} finally {
			loading.value = false;
			lazyLoading.value = false;
			isSortByDateClicked.value = false;
			// `loading` is shared with the recommendations load, which a search may not be dropped behind.
			searchV3InFlightKey = null;

			if (searchV3RetryPending) {
				searchV3RetryPending = false;
				searchJobsV3();
			}
		}
	};

	const searchJobsHandlerV3 = async () => {
		if (isSearchTextTooShort.value) {
			return;
		}

		if (isSharedLinkFound.value) {
			router.push({ name: 'job-board-search' });
			isSharedLinkFound.value = false;
		}

		if (!hasActiveSearchOrFilters.value) {
			localStorage.removeItem(SEARCH_FILTERS_STORAGE_KEY);

			if (isAuthenticated()) {
				setCurrentTab(jobResultsTabs.recommended);
			} else {
				setCurrentTab(jobResultsTabs.search);
				isFirstSearch.value = true;
				filteredJobs.value = [];
				isJobSearchFound.value = false;
			}

			return;
		}

		try {
			await nextTick();
			if (geoDataPromise.value) {
				try { await geoDataPromise.value; } catch { /* getGeoData handles its own UX */ }
			}
			setCurrentTab(jobResultsTabs.search);
			await searchJobsV3();
		} catch (error) {
			console.error('Error searching jobs:', error);
		}
	};

	// Removes a single filter chip (mobile active-filters row) and re-searches immediately —
	// live, same as clearing a filter directly in the desktop bar.
	const removeFilterChip = (key) => {
		switch (key) {
			case 'location':
				location.value = null;
				latitude.value = null;
				longitude.value = null;
				break;
			case 'range':
				selectedRange.value = RANGE_OPTIONS[0];
				break;
			case 'industries':
				selectedIndustries.value = [];
				break;
			case 'workSettings':
				selectedWorkSettings.value = [];
				break;
			case 'posted':
				selectedPosted.value = POSTED_OPTIONS[0];
				break;
			case 'experience':
				experiences.value = [minDefaultExperience, maxDefaultExperience];
				break;
			case 'salary':
				salaries.value = [minDefaultSalary, maxDefaultSalary];
				break;
			default:
				break;
		}

		searchJobsHandlerV3();
	};

	const resetSearchFiltersV3 = () => {
		jobTitle.value = null;
		location.value = null;
		latitude.value = null;
		longitude.value = null;
		selectedRange.value = RANGE_OPTIONS[0];
		selectedIndustries.value = [];
		selectedWorkSettings.value = [];
		selectedPosted.value = POSTED_OPTIONS[0];
		experiences.value = [minDefaultExperience, maxDefaultExperience];
		salaries.value = [minDefaultSalary, maxDefaultSalary];
		autoApplyFilter.value = false;
		placeholderExperiences.value = '';
		placeholderSalaries.value = '';

		searchJobsHandlerV3();
	};

	const closeMobileJobDetails = () => {
		showMobileJobDetails.value = false;
	};

	const requestJobSummary = async (job) => {
		if (!job?.aiSummaryExists && userHaveAccessTo('job-description-summary')) {
			await jobBoardApi.getJobSummary(job.provider_job_id);
		}
	};

	const setSelectedJob = async (job) => {
		showMobileJobDetails.value = true;
		selectedJob.value = job;

		await requestJobSummary(job);
		if (queryId.value) {
			await trackActivity(job.provider_job_id, 'read');
		}
	};

	const getRecommendedJobs = async () => {
		try {
			loading.value = true;
			const result = await jobBoardApi.getRecommendedJobs({
				auto_apply_filter: autoApplyFilterParam.value,
				// Only once the seeker has accepted a suggestion. The server ignores a level
				// its stack no longer has, so a set that changed underneath this is a
				// re-run ladder rather than an error.
				...(approvedRelaxationLevel.value == null
					? {}
					: { relaxation_level: approvedRelaxationLevel.value }),
			});

			if (result.query) {
				queryId.value = result.query;
			}

			recommendationsRelaxation.value = result?.relaxation ?? null;

			const jobResults = result.jobs ?? [];

			isRecommendedJobFound.value = jobResults.length > 0;
			isRecommendedJobsInProgress.value = !isRecommendedJobFound.value ? result?.in_progress : false;
			recommendedJobs.value = isRecommendedJobFound.value
				? markAdditionalFields(jobResults, result.query ?? null)
				: [];
			recommendedJobs.value = updateRecommendedJobs(null, { is_recommended: true });

			if (recommendedJobs.value.length) {
				await setSelectedJob(recommendedJobs.value[0]);
			}
		} catch {
			isRecommendedJobFound.value = false;
			recommendationsRelaxation.value = null;
			notifications.notify('Failed to get personalized jobs.', 'Error');
		} finally {
			loading.value = false;
			isRecommendedJobsLoaded.value = true;
		}
	};

	const loadRecommendedJobs = async () => {
		await getRecommendedJobs();

		selectedJob.value = recommendedJobs.value[0] ?? null;
	};

	const reloadRecommendations = async () => {
		isRecommendedJobsLoaded.value = false;
		await loadRecommendedJobs();
	};

	const getFormattedCardFilters = (job) => {
		const filterCards = [];
		const isSalaryEstimated = job?.is_salary_estimated ?? false;
		const isExperienceEstimated = job?.is_experience_estimated ?? false;

		const salaryCard = { title: '', isEstimated: isSalaryEstimated };
		const workExperienceCard = { title: '', isEstimated: isExperienceEstimated };
		const workSettingsCard = { title: '' };
		let salaryUnavailable = false;
		let workExperienceUnavailable = false;

		if (job.estimates_available) {
			if (job.work_setting) {
				workSettingsCard.title = workSettingValues[job.work_setting];
				filterCards.push(workSettingsCard);
				job.work_setting_display = workSettingValues[job.work_setting];
			}

			if (job.min_experience && job.max_experience) {
				if (job.min_experience >= job.max_experience) {
					workExperienceCard.title = `${job.min_experience}+ yrs req`;
				} else {
					workExperienceCard.title = `${job.min_experience}-${job.max_experience} yrs req`;
				}
			} else if (job.min_experience) {
				workExperienceCard.title = `${job.min_experience}+ yrs req`;
			} else if (job.max_experience) {
				workExperienceCard.title = `${job.max_experience} yrs req. max`;
			} else {
				workExperienceUnavailable = true;
			}

			if (workExperienceCard.title) {
				job.experience_display = workExperienceCard.title;
			}

			if (job.min_salary && job.max_salary) {
				salaryCard.title = `$${formatNumberWithCommas(job.min_salary)}-$${formatNumberWithCommas(
					job.max_salary
				)}`;
			} else if (job.min_salary) {
				salaryCard.title = `$${formatNumberWithCommas(job.min_salary)}+`;
			} else if (job.max_salary) {
				salaryCard.title = `$${formatNumberWithCommas(job.max_salary)} max`;
			} else {
				salaryUnavailable = true;
			}
			if (salaryCard.title) {
				job.salary_display = salaryCard.title;
			}

			if (!workExperienceUnavailable) {
				workExperienceCard.title = isExperienceEstimated
					? `Est ${workExperienceCard.title}`
					: workExperienceCard.title;
				filterCards.push(workExperienceCard);
			}

			if (!salaryUnavailable) {
				salaryCard.title = isSalaryEstimated ? `Est ${salaryCard.title}` : salaryCard.title;
				filterCards.push(salaryCard);
			}
		} else {
			filterCards.push({ title: 'Estimates available shortly', isEstimated: false });
		}

		// Sponsorship comes from the enrichment row rather than the legacy description parse,
		// so it is deliberately outside the `estimates_available` branch — a job that was never
		// parsed can still have been enriched. The "Est " prefix is the marking the v3 chip
		// relies on: it carries no provenance icon by design (EN-698), only this and a tooltip.
		if (job?.visa_sponsorship != null) {
			const isSponsorshipEstimated = job.is_sponsorship_estimated ?? false;
			const sponsorship = job.visa_sponsorship ? 'Visa sponsorship' : 'No visa sponsorship';

			job.sponsorship_display = sponsorship;
			filterCards.push({
				title: isSponsorshipEstimated ? `Est ${sponsorship}` : sponsorship,
				isEstimated: isSponsorshipEstimated,
			});
		}

		return filterCards;
	};

	const getUpdatedJobs = (job, valuesToUpdate, jobsArrayToParse) => {
		if (!job) {
			return jobsArrayToParse.map((elem) => ({ ...elem, ...valuesToUpdate }));
		}
		return jobsArrayToParse.map((elem) =>
			elem.provider_job_id === job.provider_job_id ? { ...elem, ...valuesToUpdate } : elem
		);
	};

	const updateFilteredJobs = (job, valuesToUpdate) => {
		const updatedJobs = getUpdatedJobs(job, valuesToUpdate, filteredJobs.value);

		if (
			filteredJobs.value.length < 10 &&
            SEARCH_ROUTE_NAMES.includes(route.name) &&
            infiniteScrollingPage.value < totalPages.value
		) {
			infiniteScrollingPage.value += 1;
			searchJobs(true);
		}

		return updatedJobs;
	};

	const updateRecommendedJobs = (job, valuesToUpdate) => {
		return getUpdatedJobs(job, valuesToUpdate, recommendedJobs.value);
	};

	const isJobRecommended = (job) => !!job?.is_recommended;

	/**
	 * @returns {Promise<number|null>} The saved row's id, or null when the job was not saved.
	 */
	const saveJob = async (job) => {
		if (!job) {
			notifications.notify('Failed to save the job.', 'error');
			return null;
		}

		try {
			selectedJob.value = { ...job, is_saved: true };

			if (isJobRecommended(job)) {
				recommendedJobs.value = updateRecommendedJobs(job, { is_saved: true });
			} else {
				filteredJobs.value = updateFilteredJobs(job, { is_saved: true });
			}

			const response = await jobBoardApi.saveJob(selectedJob.value);
			const createdJob = response.data;
			const jobId = createdJob.id;

			if (isJobRecommended(job)) {
				recommendedJobs.value = updateRecommendedJobs(selectedJob.value, { id: jobId });
			} else {
				filteredJobs.value = updateFilteredJobs(selectedJob.value, { id: jobId });
			}

			addJobToSaved(job, 0, { id: jobId, saved_at: createdJob.saved_at });
			selectedJob.value.id = jobId;

			notifications.notify('Job is successfully saved!');

			return jobId;
		} catch {
			if (isJobRecommended(job)) {
				recommendedJobs.value = updateRecommendedJobs(job, { is_saved: false, id: null });
			} else {
				filteredJobs.value = updateFilteredJobs(job, { is_saved: false, id: null });
			}
			notifications.notify('Failed to save the job.', 'error');

			return null;
		}
	};

	/**
	 * @returns {Promise<boolean>} Whether the job was unsaved.
	 */
	const deleteJob = async (job) => {
		if (!job || !job.id) {
			notifications.notify('Failed to unsave the job.', 'error');
			return false;
		}

		let removedJobIndex;
		let removedJob;

		try {
			if (isJobRecommended(job)) {
				recommendedJobs.value = updateRecommendedJobs(job, { is_saved: false });
			} else {
				filteredJobs.value = updateFilteredJobs(job, { is_saved: false });
			}

			if (selectedJob.value && SEARCH_ROUTE_NAMES.includes(route.name)) {
				selectedJob.value = { ...job, is_saved: false, id: null };
			}

			[removedJobIndex, removedJob] = removeJobFromSaved(job);

			await jobBoardApi.deleteJob(job?.id);

			if (filteredJobs.value.length > 0) {
				filteredJobs.value = updateFilteredJobs(job, { id: null });
			} else if (recommendedJobs.value.length > 0) {
				recommendedJobs.value = updateRecommendedJobs(job, { id: null });
			}

			notifications.notify('Job is successfully unsaved!');

			return true;
		} catch {
			// The copy Saved held, not the feed's: only it has the saved date the order needs (EN-1302).
			if (removedJobIndex != null && removedJob != null) {
				addJobToSaved(removedJob, removedJobIndex);
			}

			if (selectedJob.value && SEARCH_ROUTE_NAMES.includes(route.name)) {
				selectedJob.value = job;
			}

			if (isJobRecommended(job)) {
				recommendedJobs.value = updateRecommendedJobs(job, { is_saved: true, id: job.id });
			} else {
				filteredJobs.value = updateFilteredJobs(job, { is_saved: true, id: job.id });
			}

			notifications.notify('Failed to unsave the job.', 'error');

			return false;
		}
	};

	const passFirstEnterModal = async () => {
		if (!isJobBoardAiOnboardingPassed.value) {
			const response = await update({
				options: {
					...getUser().options,
					job_board_onboarding_passed: true,
				},
			});
			setUser(response.data.data);

			isJobBoardAiOnboardingPassed.value = true;
		}
	};

	const markFirstRunSeen = async () => {
		if (!isJobBoardFirstRunSeen.value) {
			isJobBoardFirstRunSeen.value = true;
			const response = await update({
				options: {
					...getUser().options,
					job_board_first_run_seen: true,
				},
			});
			setUser(response.data.data);
		}
	};

	const dismissUsLocationBanner = async () => {
		if (!isUsLocationBannerDismissed.value) {
			isUsLocationBannerDismissed.value = true;
			if (!isAuthenticated()) {
				return;
			}
			const response = await update({
				options: {
					...getUser().options,
					dismissed_us_location_banner: true,
				},
			});
			setUser(response.data.data);
		}
	};

	const cleanDescription = (description) => {
		return sanitizeHtml(description, {
			allowedTags: ['p', 'b', 'span', 'br', 'strong', 'em', 'i', 'div', 'li', 'ul', 'a'],
		});
	};

	const removeJobFromSaved = (jobToRemove) => {
		const indexToRemove = savedJobs.value.findIndex((job) => job.provider_job_id === jobToRemove.provider_job_id);

		if (indexToRemove === -1) {return [null, null];}

		const shownIndex = sortedSavedJobs.value.findIndex((job) => job.provider_job_id === jobToRemove.provider_job_id);
		const removedElement = savedJobs.value.splice(indexToRemove, 1)[0];

		// The job that took its place on screen opens, or the one above when it was last. The page requests the
		// summary.
		if (selectedSavedJob.value?.provider_job_id === jobToRemove.provider_job_id) {
			const shown = sortedSavedJobs.value;

			selectedSavedJob.value = shown[shownIndex] ?? shown[shownIndex - 1] ?? null;
		}

		return [indexToRemove, removedElement];
	};

	const addJobToSaved = (job, index = 0, changes = {}) => {
		savedJobs.value.splice(index, 0, {
			...job,
			timeAgoPosted: moment(job?.posted_at).fromNow(),
			is_applied: false,
			is_saved: true,
			was_saved: true,
			...changes,
		});
	};

	/** Put a removed job back where it was, or at the end when the list has shrunk since. */
	const restoreSavedJob = (removal) => {
		const index = Math.min(removal.index, savedJobs.value.length);

		addJobToSaved(removal.job, index);

		return savedJobs.value[index];
	};

	const setSavedOnListedCopies = (job, changes) => {
		filteredJobs.value = getUpdatedJobs(job, changes, filteredJobs.value);
		recommendedJobs.value = updateRecommendedJobs(job, changes);
	};

	const undoSavedRemoval = (removal) => {
		if (pendingSavedRemoval !== removal) {
			return;
		}

		pendingSavedRemoval = null;

		const restoredJob = restoreSavedJob(removal);

		if (removal.wasSelected) {
			selectedSavedJob.value = restoredJob;
		}
	};

	/**
	 * Send the DELETE once the toast has closed. Runs as the toast's onClose, which must not throw. Undo dismisses
	 * the toast without it (`runAction`), and the pending check covers any other way the two could cross.
	 */
	const commitSavedRemoval = async (removal) => {
		if (pendingSavedRemoval !== removal) {
			return;
		}

		pendingSavedRemoval = null;

		const { job } = removal;

		try {
			setSavedOnListedCopies(job, { is_saved: false, id: null });
			await jobBoardApi.deleteJob(job.id);
		} catch (error) {
			// The row is already gone, unsaved in another tab: the removal stands.
			if (error?.response?.status === 404) {
				return;
			}

			restoreSavedJob(removal);
			setSavedOnListedCopies(job, { is_saved: true, id: job.id });
			notifications.notify('Failed to remove the job from Saved.', 'error');
		}
	};

	/** Close the pending removal's toast, which sends its DELETE. Nothing happens when none is pending. */
	const commitPendingSavedRemoval = () => {
		if (pendingSavedRemoval) {
			notifications.remove(pendingSavedRemoval.toastId);
		}
	};

	/** Take a job off Saved at once, with an Undo toast. The DELETE waits for the toast to close. */
	const removeSavedJob = (job) => {
		if (Date.now() - lastSavedRemovalAt < SAVED_REMOVAL_DOUBLE_CLICK_MS) {
			return;
		}

		// One Undo at a time: the toast this replaces sends its DELETE now.
		commitPendingSavedRemoval();

		const index = savedJobs.value.findIndex((saved) => saved.provider_job_id === job?.provider_job_id);

		if (index === -1) {
			return;
		}

		const wasSelected = selectedSavedJob.value?.provider_job_id === job.provider_job_id;
		const [, removedJob] = removeJobFromSaved(job);
		const removal = { job: removedJob, index, wasSelected };

		// The toast renders its message as HTML, and titles come from external feeds.
		removal.toastId = notifications.notify(
			`${escape(removedJob.job_title)} was removed from Saved.`,
			'success',
			7000,
			null,
			'Undo',
			() => undoSavedRemoval(removal),
			{ key: 'saved-job-removed', onClose: () => commitSavedRemoval(removal) },
		);
		pendingSavedRemoval = removal;
		lastSavedRemovalAt = Date.now();
	};

	/**
	 * The on-screen half of marking a job applied: out of the recommendations (or flagged in the
	 * search results), and off the Saved list. Returns what a caller needs to undo it.
	 */
	const moveJobToApplied = (job) => {
		let removedRecommendedJob;
		let removedRecommendedIndex;

		if (selectedJob.value?.provider_job_id === job.provider_job_id) {
			selectedJob.value = { ...selectedJob.value, is_applied: true };
		}

		if (isJobRecommended(job)) {
			removedRecommendedIndex = recommendedJobs.value.findIndex(
				(j) => j.provider_job_id === job.provider_job_id
			);

			// A selected job can outlive a recommendations reload, and splice(-1) would drop
			// whichever job happens to be last.
			if (removedRecommendedIndex !== -1) {
				[removedRecommendedJob] = recommendedJobs.value.splice(removedRecommendedIndex, 1);
			}
		} else {
			filteredJobs.value = updateFilteredJobs(job, { is_saved: false, was_saved: true, is_applied: true });
		}

		const [removedJobIndex, removedSavedJob] = removeJobFromSaved(job);

		return { removedJobIndex, removedSavedJob, removedRecommendedJob, removedRecommendedIndex };
	};

	/**
	 * Show a job as applied after EN-621's "Did you apply?" confirmation, as AutoApply does.
	 *
	 * Nothing is sent: the confirmation endpoint has already recorded the application, and
	 * changeJobApplyStatus() would record it again and double-count the EN-838 snapshot. A job
	 * the store is not holding needs nothing either, because the next fetch excludes it.
	 */
	const markJobAppliedLocally = (providerJobId) => {
		const job = [...recommendedJobs.value, ...filteredJobs.value, ...savedJobs.value, selectedJob.value]
			.find((candidate) => candidate?.provider_job_id === providerJobId);

		if (!job || job.is_applied) {
			return;
		}

		moveJobToApplied(job);
	};

	const changeJobApplyStatus = async (job, status) => {
		let removedJobIndex;
		let removedSavedJob;
		let removedRecommendedJob;
		let removedRecommendedIndex;
		const wasRecommended = isJobRecommended(job);

		try {
			// The search that surfaced the job, for the EN-838 satisfaction snapshot. Null for
			// a job acted on from the Saved tab, which never came from a search response — the
			// snapshot falls back to the seeker's saved criteria there. Read before the job
			// leaves its list below, or a Saved job would fall through to the shared ref.
			const appliedFromQueryId = jobSearchContext(job?.provider_job_id).queryId;

			if (status) {
				({ removedJobIndex, removedSavedJob, removedRecommendedJob, removedRecommendedIndex } = moveJobToApplied(job));
			}

			if (job?.id) {
				await jobBoardApi.updateAppliedStatus(job.id, status, appliedFromQueryId);
			} else if (status && job?.provider_job_id) {
				await jobBoardApi.markJobApplied(job.provider_job_id, appliedFromQueryId);
			}

			const message = status ? 'Job is moved to applied!' : 'Job is removed from applied!';
			notifications.notify(message, 'success');
		} catch {
			if (status) {
				notifications.notify('Failed to move the job to applied.', 'error');
				// Each list gets back only the copy it lost: a job can be both recommended and saved, and a Search
				// job may never have been saved at all.
				if (wasRecommended && removedRecommendedJob != null) {
					recommendedJobs.value.splice(removedRecommendedIndex, 0, removedRecommendedJob);
				}

				// The copy Saved held, which has the saved date the order needs (EN-1302).
				if (removedJobIndex != null) {
					addJobToSaved(removedSavedJob, removedJobIndex);
				}
			}
		}
	};

	const removeJobFromRecommendation = async (providerJobId) => {
		let indexToRemove;
		const isLastItem = filteredJobs.value.length === 1;

		try {
			indexToRemove = filteredJobs.value.findIndex((job) => job.provider_job_id === providerJobId);

			if (isLastItem) {
				isJobSearchFound.value = false;
				isFirstSearch.value = true;
			} else {
				let indexToMove;
				filteredJobs.value.length === indexToRemove + 1 ? (indexToMove = -1) : (indexToMove = 1);

				selectedJob.value = filteredJobs.value[indexToRemove + indexToMove];
			}

			const jobsToRemove = filteredJobs.value.splice(indexToRemove, 1);
			removedJobs.value.push(...jobsToRemove);

			await jobBoardApi.hideJob(providerJobId);
			await trackActivity(providerJobId, 'hide', queryId.value);

			notifications.notify('Job is successfully hidden!', 'success', 7000, null, 'Undo', () => {
				if (isLastItem) {
					isJobSearchFound.value = true;
					isFirstSearch.value = false;
				}
				filteredJobs.value.splice(indexToRemove, 0, ...removedJobs.value);
				selectedJob.value = removedJobs.value[0];
				removedJobs.value = [];
				notifications.clear();
			});

			setTimeout(() => {
				removedJobs.value = [];
			}, 7000);
		} catch {
			notifications.notify('Failed to hide the job.', 'error');
			filteredJobs.value.splice(indexToRemove, 0, ...removedJobs);
			selectedJob.value = removedJobs.value[0];
		}
	};

	const removeRecommendedJobFromRecommendation = async (providerJobId) => {
		let indexToRemove;

		try {
			indexToRemove = recommendedJobs.value.findIndex((job) => job.provider_job_id === providerJobId);

			let indexToMove;
			recommendedJobs.value.length === indexToRemove + 1 ? (indexToMove = -1) : (indexToMove = 1);

			selectedJob.value = recommendedJobs.value[indexToRemove + indexToMove];

			const jobsToRemove = recommendedJobs.value.splice(indexToRemove, 1);
			removedJobs.value.push(...jobsToRemove);

			await jobBoardApi.hideJob(providerJobId);
			await trackActivity(providerJobId, 'hide', queryId.value);

			notifications.notify('Job is successfully hidden!', 'success', 7000, null, 'Undo', () => {
				recommendedJobs.value.splice(indexToRemove, 0, ...removedJobs.value);
				selectedJob.value = removedJobs.value[0];
				removedJobs.value = [];
				notifications.clear();
			});

			if (recommendedJobs.value.length === 0) {
				isRecommendedJobFound.value = false;
			}

			setTimeout(() => {
				removedJobs.value = [];
			}, 7000);
		} catch {
			notifications.notify('Failed to hide the job.', 'error');
			recommendedJobs.value.splice(indexToRemove, 0, ...removedJobs);
			selectedJob.value = removedJobs.value[0];
		}
	};

	/**
	 * Take a job that was just added to the AutoApply queue out of the list, and open its neighbour
	 * when it was the one on screen (EN-1236). Unlike hiding, nothing is sent to the server, there is
	 * no undo, and the seeker is left where they are: no mobile sheet opens and no `read` is recorded.
	 *
	 * A single job opened by id is the whole view, so it stays on screen and shows the queued job's button.
	 *
	 * @param {{ provider_job_id: string }} job
	 * @returns {boolean} whether the job on screen changed: its neighbour opened, or the job opened by id was
	 * replaced by its queued copy. False when the seeker had opened another job meanwhile.
	 */
	const removeQueuedJob = (job) => {
		const providerJobId = job?.provider_job_id;
		const wasSelected = selectedJob.value?.provider_job_id === providerJobId;

		if (isSharedLinkFound.value) {
			const queuedJob = { ...job, auto_apply_queue_status: 'preparing' };

			filteredJobs.value = filteredJobs.value.map(
				(listed) => (listed.provider_job_id === providerJobId ? queuedJob : listed),
			);

			if (wasSelected) {
				selectedJob.value = queuedJob;
			}

			return wasSelected;
		}

		const removeFrom = (list, isFound) => {
			const index = list.value.findIndex((listed) => listed.provider_job_id === providerJobId);

			if (index === -1) {
				return index;
			}

			list.value.splice(index, 1);

			if (list.value.length === 0) {
				isFound.value = false;
			}

			return index;
		};

		const searchIndex = removeFrom(filteredJobs, isJobSearchFound);
		const recommendedIndex = removeFrom(recommendedJobs, isRecommendedJobFound);
		const index = isSearchTab.value ? searchIndex : recommendedIndex;

		if (!wasSelected || index === -1) {
			return false;
		}

		const list = isSearchTab.value ? filteredJobs.value : recommendedJobs.value;
		const neighbour = list[index] ?? list[index - 1] ?? null;

		selectedJob.value = neighbour;

		if (neighbour) {
			// Nothing awaits this, and a missing summary is not fatal: job-details gives up on its loader by itself.
			requestJobSummary(neighbour).catch(() => {});
		}

		return true;
	};

	/**
	 * Remember the search a Job Alert just opened, so that its jobs, and those of its later pages (which share its
	 * query id), report `alert` as the AutoApply source (EN-1252). Call it after the search has run: a search that
	 * failed or came back without a query id leaves the previous search's id in `queryId`, which is not the alert's.
	 */
	const markAlertSearch = () => {
		if (isJobSearchFound.value && queryId.value !== null) {
			alertQueryId = queryId.value;
		}
	};

	/**
	 * The surface an AutoApply click came from, for the queue's analytics (EN-1252). A job opened by id, which
	 * is what a shared link and Your Journey's AutoApply link do, is a link whichever tab it is shown on.
	 *
	 * @param {object} job the job the click is for, stamped with the query that surfaced it
	 * @returns {'link'|'alert'|'recommended'|'search'}
	 */
	const autoApplySourceFor = (job) => {
		if (isSharedLinkFound.value) {
			return 'link';
		}

		if (alertQueryId !== null && job?.criteriaQueryId === alertQueryId) {
			return 'alert';
		}

		return isRecommendedTab.value ? 'recommended' : 'search';
	};

	const getGeoData = async () => {
		if (!geocoder.value) {
			return;
		}

		const gen = ++geoGeneration;

		const run = async () => {
			try {
				isGeoDataLoading.value = true;

				let timeoutId;
				const timeoutError = new Error('Geocode request timed out');
				timeoutError.isTimeout = true;
				const response = await Promise.race([
					geocoder.value.geocode({ address: location.value }),
					new Promise((_, reject) => {
						timeoutId = setTimeout(() => reject(timeoutError), 12000);
					}),
				]).finally(() => clearTimeout(timeoutId));

				if (gen !== geoGeneration) { return; }

				const geoData = response?.results[0];

				location.value = geoData?.formatted_address;
				latitude.value = geoData?.geometry?.location.lat();
				longitude.value = geoData?.geometry?.location.lng();
				lastResolvedLocation.value = location.value ?? '';
			} catch (error) {
				if (gen !== geoGeneration) { return; }

				if (error.code && error.code !== 'ZERO_RESULTS' && !error.isTimeout) {
					notifications.notify('Failed to set the location.', 'error');
				}

				latitude.value = null;
				longitude.value = null;
			} finally {
				if (gen === geoGeneration) {
					isGeoDataLoading.value = false;
					geoDataPromise.value = null;
				}
			}
		};

		geoDataPromise.value = run();
		return geoDataPromise.value;
	};

	const cancelPendingGeoData = () => {
		geoGeneration++;
		geoDataPromise.value = null;
		isGeoDataLoading.value = false;
	};

	const getLocation = async () => {
		if (!geocoder.value) {
			isGeoDataLoading.value = false;

			return;
		}

		const gen = ++geoGeneration;

		const run = async () => {
			try {
				isGeoDataLoading.value = true;

				let timeoutId;
				const timeoutError = new Error('Geocode request timed out');
				timeoutError.isTimeout = true;
				const response = await Promise.race([
					geocoder.value.geocode({ location: { lat: latitude.value, lng: longitude.value } }),
					new Promise((_, reject) => {
						timeoutId = setTimeout(() => reject(timeoutError), 12000);
					}),
				]).finally(() => clearTimeout(timeoutId));

				if (gen !== geoGeneration) { return; }

				location.value = response?.results[0]?.formatted_address;
				lastResolvedLocation.value = location.value ?? '';
				isLocationSelected.value = true;
			} catch (error) {
				if (gen !== geoGeneration) { return; }

				if (error.isTimeout) {
					return;
				}

				if (error.code && error.code !== 'ZERO_RESULTS') {
					notifications.notify('Failed to set the location.', 'error');
				}

				latitude.value = null;
				longitude.value = null;
			} finally {
				if (gen === geoGeneration) {
					isGeoDataLoading.value = false;
					geoDataPromise.value = null;
				}
			}
		};

		geoDataPromise.value = run();
		return geoDataPromise.value;
	};

	const loadSavedJobs = async () => {
		isSavedJobsLoading.value = true;

		try {
			const result = await jobBoardApi.getJobs();
			// The Saved tab, never a search response — null rather than falling back
			// to whatever search last ran elsewhere in the session (EN-838).
			savedJobs.value = markAdditionalFields(result.data).map((job) => ({ ...job, criteriaQueryId: null }));
			// Only on success, so the next visit retries a failed load.
			isSavedJobsLoaded.value = true;
		} catch {
			notifications.notify('Failed to get saved jobs.', 'error');
		} finally {
			isSavedJobsLoading.value = false;
		}
	};

	/** Tailoring on Saved writes to the Saved list, and leaves Search and Recommended alone (EN-1301). */
	const setTailoredSavedJob = (jobToTailorWith, tailoring) => {
		const index = savedJobs.value.findIndex((job) => job?.provider_job_id === jobToTailorWith.provider_job_id);
		const tailoredJob = { ...(savedJobs.value[index] ?? jobToTailorWith), ...tailoring };

		if (index !== -1) {
			savedJobs.value.splice(index, 1, tailoredJob);
		}

		selectedSavedJob.value = tailoredJob;
	};

	// Resolves whether tailored documents were produced, so the AutoApply flow can stop
	// before it spends a credit on a job the resume couldn't be tailored to (EN-894). `source` says
	// which flow asked ('auto_apply'); none is Tailor for This Job (EN-1252).
	const tailorResume = async (selectedResume, jobToTailorWith, source = null) => {
		generatingDocuments.value = true;

		try {
			const resumeId = selectedResume.uuid;
			const jobId = jobToTailorWith.provider_job_id;

			if (!resumeId || !jobId) {
				notifications.notify('Failed to generate tailored resume & cover letter', 'error');
			}

			const results = await getTailoredResume(resumeId, jobId, 'job-board', source);

			if (results?.data?.resume == null || results?.data?.coverLetter == null) {
				const explanation = results?.data?.explanation || results?.data?.['Explanation'];
				selectResumeStore.setShowUnableToTailorModal(explanation, jobToTailorWith?.job_title);

				return false;
			}

			if (route.name === 'job-board-saved') {
				setTailoredSavedJob(jobToTailorWith, { ...results.data, tailoredFromResumeUuid: resumeId });

				return true;
			}

			const jobList = currentTab.value !== 'recommended' ? filteredJobs.value : recommendedJobs.value;

			const targetJobIndex = jobList.findIndex((job) => job?.provider_job_id === jobId);
			const updatedJob = { ...jobList[targetJobIndex], ...results.data, tailoredFromResumeUuid: resumeId };

			const filteredJobsCopy = toRaw(filteredJobs.value);
			filteredJobsCopy.splice(targetJobIndex, 1, updatedJob);
			filteredJobs.value = filteredJobsCopy;
			selectedJob.value = updatedJob;

			return true;
		} catch (error) {
			// The AutoApply technical limit (EN-1314) refuses with a 429 the flow answers with its own modal.
			// On this route, only the technical-limit 429 carries a boolean has_premium_access, so the feature
			// usage 429 still toasts.
			const isTechnicalLimit =
				error?.response?.status === 429 && typeof error.response.data?.has_premium_access === 'boolean';

			if (!isTechnicalLimit) {
				notifications.notify('Failed to generate tailored resume & cover letter', 'error');
			}

			return false;
		} finally {
			generatingDocuments.value = false;
		}
	};

	const giveIndividualFeedback = (data) => jobBoardApi.giveIndividualFeedback(data);

	const deleteIndividualFeedback = (uuid) => jobBoardApi.deleteIndividualFeedback(uuid);

	const updateFeedbackComment = (uuid, comment) => jobBoardApi.updateFeedbackComment(uuid, comment);

	const feedbackOptions = () => {
		return [
			"Salary range doesn't match my expectations",
			'Location is not suitable',
			"Required skills don't match my experience",
			'Too senior for my experience level',
			'Too junior for my experience level',
			'Industry is not interesting to me',
			"Company size is not what I'm looking for",
		];
	};

	const selectNextJob = async () => {
		if (!selectedJob.value) { return; }
		const jobList = isSearchTab.value ? filteredJobs.value : recommendedJobs.value;
		const currentIndex = jobList.findIndex(
			(j) => j.provider_job_id === selectedJob.value.provider_job_id
		);
		if (currentIndex >= 0 && currentIndex < jobList.length - 1) {
			await setSelectedJob(jobList[currentIndex + 1]);
		}
	};

	/**
	 * The search that surfaced a job, and where it sat in that list.
	 *
	 * Read off the job rather than from the shared `queryId` ref, which is overwritten
	 * whenever either tab loads and so misattributes a click made after a tab round-trip
	 * (EN-838). `savedJobs` covers the Saved tab, stamped with `criteriaQueryId: null` by
	 * `loadSavedJobs()` — including it here is what lets those jobs resolve to null instead of
	 * falling through to the ref. The ref is a last resort, for a job found nowhere at all —
	 * a shared link, or one that was already spliced out of its list before this ran.
	 */
	const jobSearchContext = (providerJobId) => {
		const job = [...filteredJobs.value, ...recommendedJobs.value, ...savedJobs.value]
			.find((candidate) => candidate.provider_job_id === providerJobId);

		// A found job's own `criteriaQueryId` is authoritative even when it is null — that
		// null is `loadSavedJobs()` saying "not from a search" and must not be overwritten by the
		// ref. Only fall back to the ref when the job was not found anywhere at all.
		if (job) {
			return { queryId: job.criteriaQueryId ?? null, position: job.listPosition ?? null };
		}

		return { queryId: queryId.value ?? null, position: null };
	};

	const trackActivity = async (jobId, eventType) => {
		const context = jobSearchContext(jobId);

		await jobBoardApi.trackActivity(jobId, eventType, context.queryId, context.position);
	};

	const resetState = () => {
		// Filters
		jobTitle.value = null;
		location.value = null;
		selectedRange.value = RANGE_OPTIONS[0];
		selectedIndustry.value = INDUSTRY_OPTIONS[0];
		selectedWork.value = WORK_OPTIONS[0];
		selectedIndustries.value = [];
		selectedWorkSettings.value = [];
		selectedPosted.value = POSTED_OPTIONS[0];
		experiences.value = [minDefaultExperience, maxDefaultExperience];
		salaries.value = [minDefaultSalary, maxDefaultSalary];
		autoApplyFilter.value = false;
		placeholderExperiences.value = '';
		placeholderSalaries.value = '';
		localStorage.removeItem(SEARCH_FILTERS_STORAGE_KEY);

		// Results
		filteredJobs.value = [];
		recommendedJobs.value = [];
		savedJobs.value = [];
		selectedJob.value = null;
		selectedSavedJob.value = null;
		foundJobs.value = 0;
		removedJobs.value = [];
		pendingSavedRemoval = null;
		lastSavedRemovalAt = 0;

		// Flags
		isJobSearchFound.value = false;
		isRecommendedJobFound.value = false;
		isRecommendedJobsLoaded.value = false;
		isRecommendedJobsInProgress.value = false;
		showMobileJobDetails.value = false;
		isFirstSearch.value = true;
		isSharedLinkFound.value = false;
		isSavedSortNewestFirst.value = true;
		isSortByDateDescendent.value = true;
		isSortByRelevance.value = true;
		isSortByDateClicked.value = false;
		isJobBoardAiOnboardingPassed.value = false;
		isJobBoardFirstRunSeen.value = false;
		isUsLocationBannerDismissed.value = false;

		// Pagination
		totalPages.value = 0;
		infiniteScrollingPage.value = 1;

		// Loading
		loading.value = false;
		isSavedJobsLoading.value = false;
		lazyLoading.value = false;
		isSavedJobsLoaded.value = false;
		generatingDocuments.value = false;
		isGeoDataLoading.value = false;
		mountPageLoading.value = true;

		// Geo / query
		latitude.value = null;
		longitude.value = null;
		isLocationSelected.value = false;
		geoGeneration++;
		geoDataPromise.value = null;
		lastResolvedLocation.value = '';
		queryId.value = null;
		alertQueryId = null;
		searchCriteria.value = null;
		criteriaEnabled.value = false;
		approvedRelaxationLevel.value = null;

		// Tab
		currentTab.value = null;
	};

	return {
		jobTitle,
		location,
		queryId,
		selectedRange,
		selectedIndustry,
		selectedWork,
		selectedIndustries,
		selectedWorkSettings,
		selectedPosted,
		experiences,
		salaries,
		filteredJobs,
		recommendedJobs,
		isJobSearchFound,
		isRecommendedJobFound,
		foundJobs,
		isRecommendedJobsLoaded,
		isRecommendedJobsInProgress,
		showMobileJobDetails,
		selectedJob,
		isSortByDateDescendent,
		isSortByRelevance,
		autoApplyFilter,
		criteriaFilterState,
		searchCriteria,
		criteriaEnabled,
		completeAutoApply,
		searchRelaxation,
		recommendationsRelaxation,
		relaxationNotice,
		relaxationSuggestion,
		approvedRelaxationLevel,
		approveRelaxationSuggestion,
		loadCriteria,
		saveCriteria,
		applyRecommendedCriteria,
		applyJobAlertFilters,
		placeholderExperiences,
		placeholderSalaries,
		isFirstSearch,
		isSharedLinkFound,
		isSortByDateClicked,
		savedJobs,
		sortedSavedJobs,
		isSavedSortNewestFirst,
		selectedSavedJob,
		autocomplete,
		geocoder,
		latitude,
		longitude,
		isJobBoardAiOnboardingPassed,
		isJobBoardFirstRunSeen,
		isUsLocationBannerDismissed,
		loading,
		isSavedJobsLoading,
		lazyLoading,
		isSavedJobsLoaded,
		isGeoDataLoading,
		isLocationSelected,
		geoDataPromise,
		lastResolvedLocation,
		generatingDocuments,
		mountPageLoading,
		currentTab,
		isSearchButtonDisabled,
		isRecommendedTab,
		isSearchTab,
		isJobFound,
		activeFilterCount,
		activeFilterChips,

		resetState,
		removeFilterChip,
		searchJobs,
		getRecommendedJobs,
		loadRecommendedJobs,
		reloadRecommendations,
		saveJob,
		deleteJob,
		requestJobSummary,
		passFirstEnterModal,
		markFirstRunSeen,
		dismissUsLocationBanner,
		cleanDescription,
		changeJobApplyStatus,
		markJobAppliedLocally,
		removeJobFromRecommendation,
		removeRecommendedJobFromRecommendation,
		removeQueuedJob,
		markAlertSearch,
		autoApplySourceFor,
		setSelectedJob,
		getGeoData,
		getLocation,
		cancelPendingGeoData,
		loadSavedJobs,
		toggleSavedSort,
		removeSavedJob,
		commitPendingSavedRemoval,
		tailorResume,
		giveIndividualFeedback,
		deleteIndividualFeedback,
		markAdditionalFields,
		updateFeedbackComment,
		feedbackOptions,
		setCurrentTab,
		searchJobsHandler,
		searchJobsV3,
		searchJobsHandlerV3,
		resetSearchFiltersV3,
		persistSearchFilters,
		restoreSearchFilters,
		selectNextJob,
		trackActivity,
		closeMobileJobDetails,
	};
});
